import { useCallback } from 'react';
import { useMutation, useQuery } from 'react-query';

import { useSessionStore } from 'session/useSessionStore';
import { IResponseAPI } from 'models/interfaces/IResponseAPI';
import { ILazyDropdownOption } from 'components/lazyDropdown/LazyDropdown';
import { INameTokenViewModel } from 'models/interfaces/INameTokenViewModel';
import { axios } from 'configurations/axios';
import { ICompositeProfileInfo, SaveWellDataParams } from '../models/ICompositeProfileInfo';

export const useGetCompositeProfileInfoByQueue = (queueToken: string | undefined, enabled: boolean) => {
    const { tenantConfig, jwt } = useSessionStore((state) => ({
        tenantConfig: state.tenantConfiguration,
        jwt: state.jwtToken,
    }));

    return useQuery<ICompositeProfileInfo>(
        ['CompositeProfileInfoByQueue', queueToken],
        async () => {
            const response = await axios.get<IResponseAPI<ICompositeProfileInfo>>(
                `${tenantConfig?.endpoints.queue}/CompositeProfileUpload/GetInfoByQueue`,
                {
                    params: { queueToken },
                    headers: {
                        Authorization: 'Bearer ' + jwt,
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data.Result;
        },
        {
            enabled: enabled && !!queueToken && !!tenantConfig?.endpoints.queue,
        }
    );
};

export const useGetWellsByTerm = () => {
    const { tenantConfig, jwt } = useSessionStore((state) => ({
        tenantConfig: state.tenantConfiguration,
        jwt: state.jwtToken,
    }));

    const fetchWells = useCallback(async (term: string): Promise<ILazyDropdownOption<string>[]> => {
        const response = await axios.get<IResponseAPI<INameTokenViewModel[]>>(
            `${tenantConfig?.endpoints.queue}/CompositeProfileUpload/GetWellsByTerm`,
            {
                params: { term },
                headers: {
                    Authorization: 'Bearer ' + jwt,
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data.Result.map((item) => ({ value: item.Token, label: item.Name }));
    }, [tenantConfig?.endpoints.queue, jwt]);

    return { fetchWells };
};

export const useSaveWellData = () => {
    const { tenantConfig, jwt } = useSessionStore((state) => ({
        tenantConfig: state.tenantConfiguration,
        jwt: state.jwtToken,
    }));

    return useMutation<boolean, unknown, SaveWellDataParams>(
        async ({ compositeProfileToken, wellToken, type }) => {
            const response = await axios.post<IResponseAPI<boolean>>(
                `${tenantConfig?.endpoints.queue}/CompositeProfileUpload/SaveWellData`,
                {
                    CompositeProfileToken: compositeProfileToken,
                    WellToken: wellToken,
                    Type: type,
                },
                {
                    headers: {
                        Authorization: 'Bearer ' + jwt,
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data.Result;
        }
    );
};
